import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  message,
  Dropdown,
  Tag,
  Card,
  Popconfirm,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  getMemberListV2,
  getUmsMemberTagOptions,
  umsMemberBlock,
  umsMemberUnblock,
  umsMemberUpdateStatus,
} from 'apis/ums';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { findLabelByValue } from 'commons/options';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UmsMember } from 'types/ums';
import formatTime from 'utils/formatTime';
import { UnwrapPromise } from 'types/base';
import { DownOutlined } from '@ant-design/icons';
import useSysDict from 'commons/hooks/use-sys-dict';
import { CreateOrderTypeList } from 'constants/RecyclingConsignment';
import {
  removeRecyclingConsignmentStore,
  ITEM_NAME_MAP,
} from 'pages/rrs/recycling-consignment-order/components/utils';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { UserPlus } from 'lucide-react';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import {
  MEMBER_CREATE_SOURCE_MAP,
  MEMBER_CREATE_SOURCE_OPTION_LIST,
} from 'constant/member-create-source';
import {
  isMemberLevel,
  MEMBER_LEVEL_MAP,
  MEMBER_LEVEL_OPTION_LIST,
} from 'constant/ums-member';

const useMemberListQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [keyword, setKeyword] = useQueryState(
    'keyword',
    parseAsString.withDefault(''),
  );
  const [createSource, setCreateSource] = useQueryState(
    'createSource',
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [memberId, setMemberId] = useQueryState('memberId', parseAsInteger);
  const [memberLevel, setMemberLevel] = useQueryState(
    'memberLevel',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [tags, setTags] = useQueryState(
    'tags',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [blocked, setBlocked] = useQueryState('blocked', parseAsString);

  return {
    pageNum,
    pageSize,
    setPageNum,
    setPageSize,
    keyword,
    createSource,
    memberId,
    memberLevel,
    tags,
    blocked,
    setKeyword,
    setCreateSource,
    setMemberId,
    setMemberLevel,
    setTags,
    setBlocked,
  };
};

const MemberList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAppSelector(selectUserInfo);

  const {
    pageNum,
    pageSize,
    setPageNum,
    setPageSize,
    keyword,
    createSource,
    memberLevel,
    tags,
    memberId,
    blocked,
    setKeyword,
    setCreateSource,
    setMemberId,
    setMemberLevel,
    setTags,
    setBlocked,
  } = useMemberListQueryParams();

  const [tagOptions, setTagOptions] = useState<string[]>([]);
  useEffect(() => {
    getUmsMemberTagOptions().then((res) => {
      setTagOptions(res);
    });
  }, []);

  const tagConfig = useSysDict<[string[], { [key: string]: string }]>(
    'ums_member_tags_config',
  );

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<
      UnwrapPromise<ReturnType<typeof getMemberListV2>>['list'][number]
    >();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await getMemberListV2({
        pageNum,
        pageSize,
        keyword,
        createSource,
        memberLevel,
        tags,
        memberId: memberId ?? undefined,
        blocked: isAdmin
          ? blocked === '1'
            ? true
            : blocked === '0'
            ? false
            : undefined
          : undefined,
      });
      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    createSource,
    memberId,
    keyword,
    memberLevel,
    pageNum,
    pageSize,
    blocked,
    isAdmin,
    setDataSource,
    setLoading,
    setTotal,
    tags,
  ]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const onEdit = useCallback(
    (id: UmsMember['id']) => {
      navigate(`/ums/member-edit/${id}`);
    },
    [navigate],
  );

  const onMenuClick = useCallback(
    (key: string, { email, id }: Pick<UmsMember, 'id' | 'email'>) => {
      let url = '';
      let state = {};
      if (key === '1') {
        url = '/oms/order-create';
        state = { memberEmail: email, memberId: id };
      } else if (key === '2') {
        removeRecyclingConsignmentStore(
          ITEM_NAME_MAP.RECYCLING_CONSIGNMENT_CONTRACT,
        );
        url = '/rrs/recycling-contract-order';
        state = { memberEmail: email };
      } else if (key === '3') {
        removeRecyclingConsignmentStore(
          ITEM_NAME_MAP.RECYCLING_CONSIGNMENT_CONTRACT,
        );
        url = '/rrs/consignment-contract-order';
        state = { memberEmail: email };
      }
      navigate(url, { state });
    },
    [navigate],
  );

  const columns: ColumnsType<
    UnwrapPromise<ReturnType<typeof getMemberListV2>>['list'][number]
  > = useMemo(() => {
    const blockStatusColumn = {
      title: <Trans i18nKey={LOCALS.member_block_status} />,
      key: 'isBlocked',
      dataIndex: 'isBlocked',
      render: (isBlocked: boolean) => {
        return (
          <Tag color={isBlocked ? 'red' : 'default'}>
            <Trans
              i18nKey={
                isBlocked ? LOCALS.member_blocked : LOCALS.member_not_blocked
              }
            />
          </Tag>
        );
      },
    };

    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        render: (id: UmsMember['id']) => {
          return <a href={`/ums/member-view/${id}`}>{id}</a>;
        },
      },
      {
        title: (
          <>
            <Trans i18nKey={LOCALS.email} />
            {'/'}
            <Trans i18nKey={LOCALS.name} />
            {'/'}
            <Trans i18nKey={LOCALS.phone_number} />
          </>
        ),
        key: 'email',
        render: ({
          id,
          email,
          firstName,
          lastName,
          phone,
          countryCode,
        }: UnwrapPromise<
          ReturnType<typeof getMemberListV2>
        >['list'][number]) => {
          return (
            <div>
              <a href={`/ums/member-view/${id}`}>{email}</a>
              {[
                firstName && lastName ? `${firstName} ${lastName}` : null,
                phone && countryCode ? `+${countryCode} ${phone}` : null,
              ]
                .filter(Boolean)
                .map((item) => {
                  return <div>{item}</div>;
                })}
            </div>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.xSEiAdbHmA} />,
        dataIndex: 'last365DaysAmount',
        key: 'last365DaysAmount',
      },
      {
        title: <Trans i18nKey={LOCALS.points} />,
        key: 'integration',
        dataIndex: 'integration',
        render: (points) => {
          return points || 0;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_from} />,
        key: 'createSource',
        dataIndex: 'createSource',
        render: (createSource: number) => {
          return findLabelByValue(
            createSource || MEMBER_CREATE_SOURCE_MAP.WEBSITE,
            MEMBER_CREATE_SOURCE_OPTION_LIST,
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.member_level} />,
        key: 'memberLevel',
        dataIndex: 'memberLevel',
        render: (memberLevel: string) => {
          const COLOR_MAP = {
            [MEMBER_LEVEL_MAP.BRONZE]: '#A57E52',
            [MEMBER_LEVEL_MAP.SILVER]: '#C0C0C0',
            [MEMBER_LEVEL_MAP.GOLD]: '#FFD700',
            [MEMBER_LEVEL_MAP.PLATINUM]: '#B9B8B8',
          };
          if (!isMemberLevel(memberLevel)) return '-';

          return (
            <span
              style={{
                color: COLOR_MAP[memberLevel],
              }}
            >
              {memberLevel}
            </span>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.khvsAbbLJs} />,
        key: 'tags',
        dataIndex: 'tags',
        render: (tags?: string[]) => {
          if (!tags || tags.length === 0) return '-';

          return (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag) => {
                return (
                  <Tag color={(tagConfig && tagConfig[1][tag]) || ''} key={tag}>
                    {tag}
                  </Tag>
                );
              })}
            </div>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.status} />,
        key: 'status',
        dataIndex: 'status',
        render: (
          status: UmsMember['status'],
          {
            id,
          }: UnwrapPromise<ReturnType<typeof getMemberListV2>>['list'][number],
        ) => {
          return (
            <Switch
              checked={!!status}
              onChange={(value) => {
                umsMemberUpdateStatus({ id, status: value ? 1 : 0 }).then(
                  () => {
                    message.success(i18n.t('successful_operation'));
                    getDataSource();
                  },
                );
              }}
            />
          );
        },
      },
      ...(isAdmin ? [blockStatusColumn] : []),
      {
        title: <Trans i18nKey={LOCALS.registration_time} />,
        key: 'createTime',
        dataIndex: 'createTime',
        render: (createTime: string) => {
          return formatTime(createTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.last_login_time} />,
        key: 'lastLoginTime',
        dataIndex: 'lastLoginTime',
        render: (lastLoginTime: UmsMember['lastLoginTime']) => {
          return formatTime(lastLoginTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.points_expiration_time} />,
        key: 'pointExpireTime',
        dataIndex: 'pointExpireTime',
        render: (pointExpireTime: UmsMember['pointExpireTime']) => {
          return formatTime(pointExpireTime);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_by} />,
        key: 'createdBy',
        dataIndex: 'createdBy',
        render: (createdBy: string) => {
          return createdBy || '-';
        },
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        fixed: 'right',
        render: ({
          id,
          email,
          isBlocked,
        }: UnwrapPromise<
          ReturnType<typeof getMemberListV2>
        >['list'][number]) => {
          return (
            <div className="flex items-center gap-2 justify-center">
              <Button
                type="link"
                onClick={() => {
                  onEdit(id);
                }}
              >
                <Trans i18nKey={LOCALS.edit} />
              </Button>
              {isAdmin && (
                <Popconfirm
                  title={
                    <Trans
                      i18nKey={
                        isBlocked
                          ? LOCALS.member_unblock_confirm
                          : LOCALS.member_block_confirm
                      }
                    />
                  }
                  onConfirm={() => {
                    const action = isBlocked
                      ? umsMemberUnblock
                      : umsMemberBlock;
                    action(id).then(() => {
                      message.success(i18n.t('successful_operation'));
                      getDataSource();
                    });
                  }}
                >
                  <Button type="link" danger={!isBlocked}>
                    <Trans
                      i18nKey={
                        isBlocked ? LOCALS.member_unblock : LOCALS.member_block
                      }
                    />
                  </Button>
                </Popconfirm>
              )}
              <Dropdown
                className="px-2"
                menu={{
                  items: CreateOrderTypeList,
                  onClick: (e) => onMenuClick(e.key, { id, email }),
                }}
              >
                <a href="/" onClick={(e) => e.preventDefault()}>
                  <Space>
                    {i18n.t('create_order')}
                    <DownOutlined />
                  </Space>
                </a>
              </Dropdown>
            </div>
          );
        },
      },
    ];
  }, [tagConfig, getDataSource, onEdit, onMenuClick, isAdmin]);

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <Form layout={'inline'}>
          <Form.Item label={<Trans i18nKey={LOCALS.member_id} />}>
            <InputNumber
              value={memberId ?? undefined}
              onChange={(value) => {
                setMemberId(typeof value === 'number' ? value : null);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ minWidth: 150 }}
              min={1}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.keyword} />}>
            <Input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.created_from} />}>
            <Select
              value={createSource}
              onChange={(value) => {
                setCreateSource(value);
                setPageNum(1);
              }}
              mode="multiple"
              placeholder={i18n.t(LOCALS.please_select) || ''}
              style={{ minWidth: 150 }}
              options={MEMBER_CREATE_SOURCE_OPTION_LIST.map(
                ({ value, label }) => {
                  return {
                    value,
                    label,
                  };
                },
              )}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.member_level} />}>
            <Select
              value={memberLevel}
              onChange={(value) => {
                setMemberLevel(value);
                setPageNum(1);
              }}
              mode="multiple"
              placeholder={i18n.t(LOCALS.please_select) || ''}
              style={{ minWidth: 150 }}
              options={MEMBER_LEVEL_OPTION_LIST}
            />
          </Form.Item>

          <Form.Item label={<Trans i18nKey={LOCALS.khvsAbbLJs} />}>
            <Select
              mode="multiple"
              placeholder={i18n.t(LOCALS.please_select) || ''}
              style={{ minWidth: 150 }}
              options={tagOptions.map((tag) => ({
                value: tag,
                label: tag,
              }))}
              value={tags}
              onChange={(value) => {
                setTags(value);
                setPageNum(1);
              }}
            />
          </Form.Item>

          {isAdmin && (
            <Form.Item label={<Trans i18nKey={LOCALS.member_block_status} />}>
              <Select
                allowClear
                placeholder={i18n.t(LOCALS.please_select) || ''}
                style={{ minWidth: 150 }}
                value={blocked ?? undefined}
                onChange={(value) => {
                  setBlocked(value ?? null);
                  setPageNum(1);
                }}
                options={[
                  { value: '1', label: i18n.t(LOCALS.member_blocked) },
                  { value: '0', label: i18n.t(LOCALS.member_not_blocked) },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  setKeyword('');
                  setCreateSource([]);
                  setMemberId(null);
                  setMemberLevel([]);
                  setTags([]);
                  setBlocked(null);
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  navigate('/ums/member-add');
                }}
                className="flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                <Trans i18nKey={LOCALS.add} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          tableLayout="auto"
          scroll={{ x: 'max-content' }}
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            onChange: (page, pageSize) => {
              setPageNum(page);
              setPageSize(pageSize);
            },
          }}
          loading={loading}
          rowKey={'id'}
          dataSource={dataSource}
          columns={columns}
        />
      </Card>
    </div>
  );
};

export default MemberList;
